import { deriveTriState } from '../../mapLayers/lib/zoneLandData/confidence';
import {
  landClassDescription,
  landClassSentence,
} from '../../mapLayers/data/landClassDescriptions';
// The two whole-classification links are the same two links the map legend
// shows, pointing at the same two URLs, so they share one set of words. They
// used to say 'Soil types of England and Wales' and "Guide to Scotland's soil
// types" here while the legend said something else again, which is the same
// farmer reading two names for one destination on one screen (11/09/2026).
import { STRINGS as SOIL_LEGEND_STRINGS } from '../../mapLayers/lib/soilLegend';

// Priorities list the most-detailed / correct source first. pickBestLayer with
// { preferData: true } then prefers whichever priority layer actually returned
// data — this is what lets a Welsh zone use alcWales even though the overlapping
// England ALC layer is also in coverage (but returns no features).
const SOIL_LAYER_PRIORITY = ['soilScotland25k', 'soilScotland250k', 'soilEnglandWales'];
const LCA_LAYER_PRIORITY = ['lcaScotland', 'alcEngland', 'alcWales'];

const DESIGNATION_LAYERS = [
  { layerId: 'sssiEngland', label: 'SSSI' },
  { layerId: 'sacEngland', label: 'SAC' },
  { layerId: 'spaEngland', label: 'SPA' },
  { layerId: 'sssiScotland', label: 'SSSI' },
  { layerId: 'sacScotland', label: 'SAC' },
  { layerId: 'spaScotland', label: 'SPA' },
  { layerId: 'sssiWales', label: 'SSSI' },
  { layerId: 'sacWales', label: 'SAC' },
  { layerId: 'spaWales', label: 'SPA' },
];

const NVZ_LAYER_PRIORITY = ['nvzScotland', 'nvzEngland'];

const NVZ_WORDS = {
  none: 'Not in an NVZ',
  partial: 'Partly in an NVZ',
  full: 'In an NVZ',
};

// Wales revoked its Nitrate Vulnerable Zone designations in April 2021 (Water
// Resources (Control of Agricultural Pollution) (Wales) Regulations 2021): the
// all-Wales agricultural pollution rules now apply to every farm, so there is no
// per-field NVZ lookup to do. Reading England's NVZ layer for a Welsh zone gives
// a false "Not in an NVZ" (its bbox bleeds over the border and returns nothing),
// so for Welsh zones we skip the lookup and show the rules + guidance link.
// Confirmed 11/09/2026. It is NOT accurate to tell a Welsh farmer their field
// "is in an NVZ" post-2021;
// the framing is that the Wales-wide rules apply.
const WELSH_AG_POLLUTION_RULES = {
  title: 'Regulatory zones',
  label: 'Wales-wide farming rules apply',
  sourceLink: {
    label: 'Control of agricultural pollution rules (Welsh Government)',
    url: 'https://www.gov.wales/control-agricultural-pollution-regulations-guidance',
  },
};

// Region-specific row titles. Scotland and England/Wales use genuinely different
// schemes, so the title follows whichever layer supplied the data.
// Confirmed 11/09/2026: each nation keeps its own scheme's name rather than
// being flattened to a single shared one.
function landCapabilityTitle(layerId) {
  return layerId && layerId.startsWith('lca') ? 'Land capability' : 'Land classification';
}

function nvzTitle(layerId) {
  // Scotland: Nitrate Vulnerable Zones only. England: NVZ + drinking-water
  // Safeguard Zones, so an umbrella term. "Regulatory zones" confirmed
  // 11/09/2026: each region names what it actually has.
  return layerId === 'nvzScotland' ? 'Nitrate Vulnerable Zones' : 'Regulatory zones';
}

// Provenance shown as a small "Based on …" footer under the graded rows (soil,
// land capability). One home for the source + scale so the wording stays consistent.
const SOURCE_NOTES = {
  soilEnglandWales: 'Soilscapes at 1:250,000',
  soilScotland25k: "the James Hutton Institute's soil data at 1:25,000",
  soilScotland250k: "the James Hutton Institute's soil data at 1:250,000",
  // One key for both Scottish scales: the Hutton Hybrid service returns 1:50,000
  // where that survey exists and 1:250,000 elsewhere, so the footer names the
  // publisher and leaves the scale out rather than stating one that may be wrong
  // for this particular zone.
  lcaScotland: "the James Hutton Institute's land capability data",
  alcEngland: "Natural England's Provisional ALC at 1:250,000",
  alcWales: "the Welsh Government's Predictive ALC",
};

const FLOOD_NONE = 'No flood risk';

// FLOOD RISK MODEL. Rendering + legend now use consistent risk-map products
// across England (RoFRS / RoFSW) and Wales (Flood Risk Assessment Wales), all on
// a High/Medium/Low scale. Panel detection: Wales (rivers/sea/surface) and
// England surface water read the risk products directly; England rivers/sea uses
// the planning Flood Zones (vector) mapped via the EA's own plain-English
// descriptors (FZ3 = High Probability, FZ2 = Medium) because RoFRS is
// raster-only and cannot be feature-queried per zone.
// ACCEPTED 11/09/2026, not an open question: for England rivers/sea the map
// (risk product) and the panel (planning zones) rest on different underlying
// probabilities though both read as High/Medium. Each label is the publisher's
// own word for its own product, so each is accurate on its own terms, which is
// the same rule that stops the nations being unified.
const FLOOD_SEVERITY_RANK = { high: 3, medium: 2, low: 1 };
const FLOOD_SEVERITY_WORD = {
  high: 'High flood risk',
  medium: 'Medium flood risk',
  low: 'Low flood risk',
};
// Concise per-source wording for the expandable breakdown in the panel.
const FLOOD_BREAKDOWN_WORD = { high: 'High risk', medium: 'Medium risk', low: 'Low risk' };
const FLOOD_MIN_FRACTION = 0.01; // ignore polygon-clip slivers

// Scotland panel copy (BBB-457): name the flood type + medium likelihood in
// plain words — matches the SEPA medium map tiles the farmer sees.
const SCOTLAND_FLOOD_TYPE_PHRASE = {
  River: 'river',
  Coastal: 'coastal',
  'Surface water': 'surface water',
};

function scotlandFloodLabel(source) {
  const typePhrase = SCOTLAND_FLOOD_TYPE_PHRASE[source] || 'flood';
  return `In a ${typePhrase} flood zone (medium likelihood)`;
}

function scotlandFloodBreakdownLabel(source) {
  const typePhrase = SCOTLAND_FLOOD_TYPE_PHRASE[source] || 'flood';
  return `${typePhrase.charAt(0).toUpperCase()}${typePhrase.slice(1)} (medium likelihood)`;
}

// Class code -> severity. England planning zones use the EA descriptors; the risk
// products use High/Medium/Low (matched case-insensitively).
const EA_FLOOD_ZONE = { FZ3: 'high', FZ2: 'medium' };
const RISK_BAND = { high: 'high', medium: 'medium', low: 'low' };

// Per-nation "how flood risk is assessed" pages, shown as a source link under the
// Zone Details flood row instead of the old "~X% likely" annual-chance caption —
// per-nation methodology differs (England rivers/sea uses the planning Flood
// Zones, not the RoFRS/RoFSW risk bands — see FLOOD_LAYERS below), so a single
// percentage figure risked overstating precision. Links verified against the
// official domain for each region (BBB-456). This is the single source of truth
// for these links — the map legend (legend-metadata.js) renders swatches only.
// Wording confirmed 11/09/2026: one shape per nation, each naming its own body.
const FLOOD_SOURCE_LINK = {
  england: {
    label: 'How flood risk is assessed (Environment Agency)',
    url: 'https://www.gov.uk/guidance/flood-risk-and-coastal-change',
  },
  scotland: {
    label: 'How flood risk is assessed (SEPA)',
    url: 'https://www.sepa.org.uk/flood-map-help-pages/likelihood-of-flooding/',
  },
  wales: {
    label: 'How flood risk is assessed (Natural Resources Wales)',
    url: 'https://naturalresources.wales/flooding/managing-flood-risk/understanding-your-flood-risk-results/?lang=en',
  },
};

function severityForClass(classSeverity, code) {
  if (code == null) return null;
  const key = String(code);
  return classSeverity[key] || classSeverity[key.toLowerCase()] || null;
}

// Each flood layer's class->severity map (graded) or fixed severity (categorical),
// plus the source group it rolls up to in the panel breakdown (rivers/sea vs
// surface water). Coastal groups under "Rivers & sea".
const FLOOD_LAYERS = [
  { layerId: 'floodZonesEngland', kind: 'graded', classSeverity: EA_FLOOD_ZONE, source: 'Rivers & sea', region: 'england' },
  { layerId: 'floodRiversWales', kind: 'graded', classSeverity: RISK_BAND, source: 'Rivers & sea', region: 'wales' },
  { layerId: 'floodSeaWales', kind: 'graded', classSeverity: RISK_BAND, source: 'Rivers & sea', region: 'wales' },
  { layerId: 'floodSurfaceWaterWales', kind: 'graded', classSeverity: RISK_BAND, source: 'Surface water', region: 'wales' },
  // Scotland: type-specific sources at medium likelihood (matches map tiles).
  { layerId: 'floodRiverScotland', kind: 'categorical', severity: 'medium', source: 'River', region: 'scotland' },
  { layerId: 'floodSurfaceScotland', kind: 'categorical', severity: 'medium', source: 'Surface water', region: 'scotland' },
  { layerId: 'floodCoastalScotland', kind: 'categorical', severity: 'medium', source: 'Coastal', region: 'scotland' },
];

function layerHasData(result) {
  if (!result || result.status !== 'ok') return false;
  if (Array.isArray(result.classes)) return result.classes.length > 0;
  return (result.coveredFraction || 0) > 0;
}

function pickBestLayer(layers, priority, options = {}) {
  const skipErrors = Boolean(options.skipErrors);
  const preferData = Boolean(options.preferData);

  // First pass (graded rows): the highest-priority layer that actually
  // returned data. Prevents an in-coverage-but-empty layer (e.g. England ALC
  // over a Welsh zone) from shadowing the correct regional source.
  if (preferData) {
    for (const layerId of priority) {
      const result = layers?.[layerId];
      if (layerHasData(result)) {
        return { layerId, result };
      }
    }
  }

  for (const layerId of priority) {
    const result = layers?.[layerId];
    if (!result || result.status === 'out-of-coverage') {
      continue;
    }
    if (skipErrors && result.status === 'error') {
      continue;
    }
    return { layerId, result };
  }
  return null;
}

// classLabel lived here for formatLcaHeadline. GradedLayerRow owns the only
// copy now, because the row renders the class names itself.

// Wales' predictive ALC returns a bare grade string ("5"); England's ALC and
// the farmer expect "Grade 5". Prefix bare ALC grades so the row reads
// consistently across nations. Non-grade codes pass through untouched.
// Confirmed 11/09/2026: the prefix stays, including for Wales' 3a and 3b.
function normaliseWelshAlcClasses(classes) {
  if (!Array.isArray(classes)) return classes;
  return classes.map((entry) => {
    const code = entry.code != null ? String(entry.code) : '';
    if (/^[1-5][ab]?$/i.test(code)) {
      const graded = `Grade ${code}`;
      return { ...entry, code: graded, name: graded };
    }
    return entry;
  });
}

// Returns { label, status }:
//  - status 'ok'      → label is a confirmed result ("None" or "SSSI + SPA")
//  - status 'unknown' → no confirmed hit AND a covering source errored, so we
//    can't honestly say "None" (the region's own layer might have held a hit we
//    never saw). Surfaced as "Couldn't check" rather than a false "None".
export function combineDesignations(layers) {
  const hits = [];
  let anyError = false;
  for (const { layerId, label } of DESIGNATION_LAYERS) {
    const result = layers?.[layerId];
    if (!result || result.status === 'out-of-coverage') {
      continue;
    }
    if (result.status !== 'ok') {
      anyError = true;
      continue;
    }
    // Same label (e.g. SSSI) can appear per nation — only list it once.
    if (deriveTriState(result.coveredFraction) !== 'none' && !hits.includes(label)) {
      hits.push(label);
    }
  }
  if (hits.length > 0) {
    return { label: hits.join(' + '), status: 'ok' };
  }
  if (anyError) {
    return { label: "Couldn't check", status: 'unknown' };
  }
  return { label: 'None', status: 'ok' };
}

function triStateLabel(layers, priority, words) {
  const picked = pickBestLayer(layers, priority);
  if (!picked || picked.result.status !== 'ok') {
    return { status: picked?.result?.status || 'missing', label: null, layerId: picked?.layerId || null };
  }
  const triState = deriveTriState(picked.result.coveredFraction);
  return {
    status: 'ok',
    label: words[triState],
    layerId: picked.layerId,
  };
}

// Consolidate every in-coverage flood layer into one worst-case line:
// "<severity> — <n>% of field (<zone>)". Returns null when no flood layer
// covers the zone (row hidden), a "No flood risk" line when covered but clear,
// or { status: 'error' } when every covering flood layer failed.
export function aggregateFlood(layers) {
  let anyInCoverage = false;
  let okCount = 0;
  let anyTruncated = false;
  let best = null; // { rank, severity, fraction }
  const groups = new Map(); // source -> { rank, severity, fraction }

  const consider = (source, severity, fraction, region) => {
    const rank = FLOOD_SEVERITY_RANK[severity];
    if (!rank || (fraction || 0) < FLOOD_MIN_FRACTION) return;
    if (!best || rank > best.rank || (rank === best.rank && fraction > best.fraction)) {
      best = { rank, severity, fraction, region, source };
    }
    const group = groups.get(source);
    if (!group || rank > group.rank || (rank === group.rank && fraction > group.fraction)) {
      groups.set(source, { rank, severity, fraction, region });
    }
  };

  for (const cfg of FLOOD_LAYERS) {
    const result = layers?.[cfg.layerId];
    if (!result || result.status === 'out-of-coverage') continue;
    anyInCoverage = true;
    if (result.status !== 'ok') continue;
    okCount += 1;
    if (result.truncated) anyTruncated = true;
    if (cfg.kind === 'graded') {
      for (const entry of result.classes || []) {
        const severity = severityForClass(cfg.classSeverity, entry.code);
        if (severity) consider(cfg.source, severity, entry.areaFraction, cfg.region);
      }
    } else if (deriveTriState(result.coveredFraction) !== 'none') {
      consider(cfg.source, cfg.severity, result.coveredFraction, cfg.region);
    }
  }

  if (!anyInCoverage) return null;
  if (okCount === 0) return { title: 'Flood risk', status: 'error', label: null };
  if (!best) return { title: 'Flood risk', status: 'ok', label: FLOOD_NONE, truncated: anyTruncated };

  const pct = Math.max(1, Math.round(best.fraction * 100));
  // One breakdown row per source group carrying a risk, worst-first, for the
  // expandable detail in the Zone Details panel (rivers/sea vs surface water).
  const breakdown = [...groups.entries()]
    .sort((a, b) => b[1].rank - a[1].rank || b[1].fraction - a[1].fraction)
    .map(([source, group]) => ({
      source,
      severity: group.severity,
      label:
        group.region === 'scotland'
          ? scotlandFloodBreakdownLabel(source)
          : FLOOD_BREAKDOWN_WORD[group.severity],
    }));

  const label =
    best.region === 'scotland'
      ? scotlandFloodLabel(best.source)
      : `${FLOOD_SEVERITY_WORD[best.severity]} — ${pct}% of field`;

  return {
    title: 'Flood risk',
    status: 'ok',
    severity: best.severity,
    label,
    sourceLink: best.region ? FLOOD_SOURCE_LINK[best.region] : undefined,
    breakdown,
    truncated: anyTruncated,
  };
}

// Where the farmer goes to read about the soil under their field, with the link
// text saying what they will actually get. Soilscapes deep-links per type via
// SS_ID (carried on the class as `link`); everything else lands on a page that
// describes the whole classification, and says so.
//
// Scotland changed on 20/08/2026. It pointed at soilfinder.hutton.ac.uk, which
// serves an untrusted certificate chain (schannel SEC_E_UNTRUSTED_ROOT), so a
// farmer tapping it met a browser security warning rather than a soil
// description — and it was a map viewer, not the classification. The Scottish
// Government's own guide to soil types describes all of them and resolves
// cleanly. There is still no per-soil Scottish page: the guide has no per-type
// anchors, checked 20/08/2026, so the label promises a guide and not a type.
const SOILSCAPES_LIST_URL = 'https://www.landis.org.uk/soilsguide/soilscapes_list.cfm';
export const SCOTLAND_SOIL_GUIDE_URL =
  'https://soils.environment.gov.scot/soils-in-scotland/guide-to-soil-types/';

// SS_ID arrives from the Cranfield WFS response, so it is third-party data going
// into a URL we hand a farmer. The origin is hard-coded, so a hostile value could
// not change the host or the scheme, but it could still append query parameters.
// Anything that is not a plain positive integer falls back to the list page:
// stricter than encoding it, and the fallback already exists for `0` and absent.
const SSID_PATTERN = /^[1-9][0-9]*$/;

export function getSoilLearnMore(layerId, dominantClass) {
  if (layerId === 'soilEnglandWales') {
    // Coerced rather than type-checked: computeGradedBreakdown already does
    // String(...) on this field, but a number arriving from anywhere else must
    // still deep-link rather than silently drop every farmer onto the list page.
    const ssid = dominantClass?.link == null ? '' : String(dominantClass.link);
    if (SSID_PATTERN.test(ssid)) {
      return {
        url: `https://www.landis.org.uk/soilsguide/soilscapes.cfm?ssid=${encodeURIComponent(ssid)}`,
        label: 'Learn more about this soil type',
      };
    }
    return {
      url: SOILSCAPES_LIST_URL,
      label: SOIL_LEGEND_STRINGS.fullKeyLink,
    };
  }
  if (layerId?.startsWith('soilScotland')) {
    return {
      url: SCOTLAND_SOIL_GUIDE_URL,
      label: SOIL_LEGEND_STRINGS.soilGuideLink,
    };
  }
  return {
    url: SOILSCAPES_LIST_URL,
    label: SOIL_LEGEND_STRINGS.fullKeyLink,
  };
}

export function aggregateLandSummary(layerData) {
  const layers = layerData?.layers || {};
  const summary = {
    soil: null,
    landCapability: null,
    nvz: null,
    designation: null,
    flood: null,
  };

  const soilPick = pickBestLayer(layers, SOIL_LAYER_PRIORITY, {
    skipErrors: true,
    preferData: true,
  });
  if (soilPick) {
    summary.soil = {
      ...soilPick,
      title: 'Soil type',
      source: SOURCE_NOTES[soilPick.layerId] || null,
      learnMore: getSoilLearnMore(
        soilPick.layerId,
        soilPick.result.classes?.[0]
      ),
    };
  }

  const lcaPick = pickBestLayer(layers, LCA_LAYER_PRIORITY, {
    skipErrors: true,
    preferData: true,
  });
  if (lcaPick) {
    const classes =
      lcaPick.layerId === 'alcWales'
        ? normaliseWelshAlcClasses(lcaPick.result.classes)
        : lcaPick.result.classes;
    // A grade shown to a farmer as a bare "3.1" says nothing, so each class
    // carries the few words its own provider publishes for that level (Davin's
    // call, 21/08). Attached here rather than looked up in the row, so the row
    // stays a dumb molecule with no knowledge of which layer it is drawing.
    //
    // The fuller published sentence goes on the DOMINANT class only (index 0),
    // and only where that provider publishes one, which today means Wales. Every
    // other class keeps the few words alone, so a mixed zone does not become a
    // wall of text (Davin, 11/09/2026).
    const describedClasses = Array.isArray(classes)
      ? classes.map((entry, index) => {
          const code = entry.code ?? entry.name;
          const sentence =
            index === 0 ? landClassSentence(lcaPick.layerId, code) : null;
          return {
            ...entry,
            description: landClassDescription(lcaPick.layerId, code),
            ...(sentence ? { sentence } : {}),
          };
        })
      : classes;
    summary.landCapability = {
      ...lcaPick,
      title: landCapabilityTitle(lcaPick.layerId),
      source: SOURCE_NOTES[lcaPick.layerId] || null,
      // `classes` is normalised for Wales ("5" -> "Grade 5") and carries the
      // per-level wording, so the row must read this copy rather than the raw
      // result it came from.
      result: { ...lcaPick.result, classes: describedClasses },
    };
  }

  // A Welsh zone is detected from the Welsh predictive ALC returning data — the
  // one Wales-only source that reliably has features (its bbox does not bleed
  // useful data into England/Scotland). See WELSH_AG_POLLUTION_RULES above.
  // Edge case: a Welsh field with no ALC data falls through to the England NVZ
  // layer and can still read a false "Not in an NVZ". Rare, but real: Wales
  // revoked its NVZ designations in April 2021 and all-Wales rules now apply to
  // every farm, so telling a Welsh farmer they are or are not in an NVZ is
  // wrong either way. See the KNOWN LIMITATIONS block in
  // mapLayers/lib/zoneLandData/layers.js.
  if (layerHasData(layers?.alcWales)) {
    summary.nvz = { ...WELSH_AG_POLLUTION_RULES, status: 'ok' };
  } else {
    const nvz = triStateLabel(layers, NVZ_LAYER_PRIORITY, NVZ_WORDS);
    if (nvz.layerId) {
      summary.nvz = { ...nvz, title: nvzTitle(nvz.layerId) };
    }
  }

  const hasDesignationLayer = DESIGNATION_LAYERS.some(
    ({ layerId }) => layers[layerId] && layers[layerId].status !== 'out-of-coverage'
  );
  if (hasDesignationLayer) {
    const designation = combineDesignations(layers);
    summary.designation = {
      title: 'Protected site',
      label: designation.label,
      status: designation.status,
    };
  }

  summary.flood = aggregateFlood(layers);

  return summary;
}

export function hasLandSummaryContent(summary) {
  return Boolean(
    summary.soil ||
      summary.landCapability ||
      summary.nvz ||
      summary.designation ||
      summary.flood
  );
}
